import React, { useMemo, useRef, useEffect, useCallback } from "react";
import { format } from "date-fns";
import { Location, Trip, TripUser } from "@/types";
import { getFlagImage, formatImageUrl } from "@/lib/general";
import { DateTime } from "luxon";
import DayItem from "@tripbuilder/cat-overview/right/DayItem";
import { useSelectedDayItems } from "@/hooks/useSelectedDayItems";
import useTransportStore from "@/stores/tripBuilder/useTransport";
import useOriginStore from "@/stores/tripBuilder/useOrigin";
import LazyLoad from "@tripbuilder/image/LazyLoad";
import TransportCard from "./TransportCard";
import { AvatarImage } from "@/components/ui/DefaultAvatar";
import usePresenceStore from "@/stores/tripBuilder/usePresence";
import logger from "@/utils/logger";

interface LocationHeaderProps {
  location: Location;
  dates: {
    start: string;
    end: string;
  };
  isFirstLocation: boolean;
  locationRef: React.RefObject<HTMLDivElement>;
  tripUsers?: TripUser[];
}

const LocationHeader: React.FC<LocationHeaderProps> = ({
  location,
  dates,
  isFirstLocation,
  locationRef,
  tripUsers = [],
}) => {
  const startDate = new Date(dates.start);
  const endDate = dates.end ? new Date(dates.end) : null;
  const { selectedLocation } = useOriginStore();
  const isSelected = selectedLocation?.id === location.id;
  const activeUsers = usePresenceStore((state) => state.activeUsers);

  // Log when this component renders
  useEffect(() => {
    logger.log(
      `LocationHeader rendered for ${location.name} with ID ${location.id}`
    );

    // Log when the ref is attached
    if (locationRef.current) {
      logger.log(`Ref attached for location ${location.id}`);
    } else {
      logger.log(`Ref NOT attached for location ${location.id}`);
    }
  }, [location.id, location.name, locationRef]);

  const formatDateRange = () => {
    if (isFirstLocation) {
      return `Departing ${format(startDate, "dd MMM")}`;
    } else if (endDate) {
      return `${format(startDate, "dd MMM")} - ${format(
        endDate,
        "dd MMM yyyy"
      )}`;
    }
    return format(startDate, "PP"); // Fallback, shouldn't normally happen
  };

  // Get location participants
  const locationParticipants = useMemo(() => {
    if (!location.participants || !tripUsers.length) return [];

    return location.participants.map((participant) => {
      // Find the corresponding trip user
      const tripUser = tripUsers.find(
        (user) => user.user_id === participant.id
      );

      return {
        id: participant.id,
        name:
          participant.name ||
          (tripUser
            ? `${tripUser.first_name} ${tripUser.last_name}`
            : "Unknown User"),
        profile_image:
          participant.profile_image || tripUser?.profile_image || "",
        isActive: activeUsers.some((user) => user.user_id === participant.id),
        firstName:
          tripUser?.first_name || participant.name?.split(" ")[0] || "",
        lastName: tripUser?.last_name || participant.name?.split(" ")[1] || "",
      };
    });
  }, [location.participants, tripUsers, activeUsers]);

  return (
    <div className="p-1">
      <div
        className={`group relative ${!location.origin ? "mb-4" : ""} ${
          isSelected ? "ring-1 ring-indigo-500 ring-offset-2 rounded-xl" : ""
        }`}
        ref={locationRef}
        data-location-id={location.id}
        id={`location-${location.id}`}
      >
        <div className="relative h-52 w-full overflow-hidden rounded-xl">
          {/* Image with parallax hover effect */}
          <LazyLoad
            src={formatImageUrl(location.image_url, "high")}
            lowResSrc={formatImageUrl(location.image_url, "low")}
            alt={`${location.name}`}
            className="w-full h-full object-cover"
          />

          {/* Content Container */}
          <div className="absolute inset-0 py-3 flex flex-col justify-between bg-gradient-to-b from-transparent via-black/20 to-black/80">
            {/* Top Section */}
            <div className="flex justify-between items-start px-3">
              <div className=" flex items-center gap-1 backdrop-blur-lg bg-white/20 rounded-full pr-4 h-7">
                <img
                  src={getFlagImage(location.address_components.country)}
                  alt="Country flag"
                  className="w-7 h-7 rounded-full border-4 border-transparent"
                />
                <span className="text-white font-light text-xs">
                  {location.address_components.country}
                </span>
              </div>

              {/* Date Badge */}
              <div className="bg-white/90 backdrop-blur-sm px-4 h-7 flex items-center rounded-full shadow-sm">
                <div className="flex items-center gap-3">
                  <span className="text-neutral-700 font-light text-xs">
                    {formatDateRange()}
                  </span>
                </div>
              </div>
            </div>

            {/* Bottom Section */}
            <div className="flex justify-between items-end px-4 rounded-lg">
              <h2 className="text-3xl font-bold text-white">{location.name}</h2>

              {/* Location Participants */}
              {locationParticipants.length > 0 && (
                <div className="flex items-center mb-2">
                  {locationParticipants
                    .sort((a, b) => {
                      if (a.isActive && !b.isActive) return -1;
                      if (!a.isActive && b.isActive) return 1;
                      return 0;
                    })
                    .map((participant, index) => (
                      <div
                        key={participant.id}
                        className={`relative w-7 h-7 rounded-full ring-2 ring-white ${
                          index !== 0 ? "-ml-2" : ""
                        }`}
                        style={{
                          zIndex: locationParticipants.length - index,
                        }}
                      >
                        <AvatarImage
                          src={participant.profile_image}
                          firstName={participant.firstName}
                          lastName={participant.lastName}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface DayViewProps {
  date: string;
  locationTimeZone: string;
  isLastDay: boolean;
  selectedLocation: Location | null;
  nextLocation: Location | null;
  previousLocation: Location | null;
  handleTransportButtonClick: (location: Location) => void;
  effectiveArrivalDate: string;
}

const DayView: React.FC<DayViewProps> = ({
  date,
  locationTimeZone,
  isLastDay,
  selectedLocation,
  nextLocation,
  previousLocation,
  handleTransportButtonClick,
  effectiveArrivalDate,
}) => {
  const formattedDate = DateTime.fromISO(date).toFormat("d MMM");
  const items = useSelectedDayItems(date, selectedLocation?.id);

  const dayNumber = useMemo(() => {
    const currentDate = DateTime.fromISO(date)
      .setZone(locationTimeZone, { keepLocalTime: true })
      .startOf("day");

    const arrivalDate =
      DateTime.fromISO(effectiveArrivalDate).setZone(locationTimeZone);

    if (currentDate.hasSame(arrivalDate, "day")) {
      return "Arrival Day";
    }

    if (isLastDay) {
      return "Departure Day";
    }

    const daysDiff = currentDate.diff(arrivalDate, "days").days;
    const roundedDaysDiff = Math.ceil(daysDiff);

    if (roundedDaysDiff <= 0) {
      return "Travel Day";
    } else {
      return `Day ${roundedDaysDiff}`;
    }
  }, [date, effectiveArrivalDate, locationTimeZone, isLastDay]);

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      if (item.name.startsWith("Staying at:")) {
        return false;
      }
      return true;
    });
  }, [items]);

  return (
    <div className="mb-6">
      <div className="w-full flex flex-row items-center mb-4 transition ease-in-out duration-300 space-x-4 group pt-1 pr-2">
        <div className="flex flex-row min-w-[90px] day-button py-1.5 bg-indigo-100/75 rounded-md hover:bg-indigo-200/75 transition duration-300 ease-in-out items-center justify-center">
          <h3 className="w-full day-number text-[14px] font-semibold tracking-tight text-indigo-800 text-center">
            {formattedDate}
          </h3>
        </div>
        <div className="h-[0.6px] rounded-full bg-neutral-300 group-hover:bg-neutral-600 w-[93%] transition ease-in-out duration-300" />
        <h3 className="text-neutral-300 whitespace-nowrap day-date text-sm font-normal tracking-tight group-hover:text-neutral-700 transition ease-in-out duration-300 py-2 rounded-lg">
          {dayNumber}
        </h3>
      </div>
      <div className="space-y-4">
        {filteredItems.length === 0 ? (
          <div className="bg-neutral-50 rounded-lg p-6 text-center">
            <p className="text-sm text-neutral-500 font-light">
              No activities planned for this day
            </p>
          </div>
        ) : (
          filteredItems.map((item, index) => (
            <DayItem
              key={`${item.id}-${index}`}
              activity={item}
              selectedDay={date}
              index={index}
              locationTimeZone={locationTimeZone}
              handleTransportButtonClick={handleTransportButtonClick}
              nextLocation={nextLocation}
              selectedLocation={selectedLocation}
              previousLocation={previousLocation}
              showLocationName={false}
            />
          ))
        )}
      </div>
    </div>
  );
};

// Debounce function to limit how often a function can be called
const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

// Throttle function to limit how often a function can be called
const throttle = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let lastCall = 0;
  return (...args: Parameters<T>) => {
    const now = Date.now();
    if (now - lastCall >= wait) {
      lastCall = now;
      func(...args);
    }
  };
};

interface TripItineraryProps {
  selectedTrip: Trip | null;
  isNavigating?: boolean;
  handleTransportButtonClick: (location: Location) => void;
}

const TripItinerary: React.FC<TripItineraryProps> = ({
  selectedTrip,
  isNavigating = false,
  handleTransportButtonClick,
}) => {
  const { getEffectiveArrivalDate } = useTransportStore();
  const { selectedLocation, setSelectedLocation } = useOriginStore();
  const locationRefs = useRef<Map<string, React.RefObject<HTMLDivElement>>>(
    new Map()
  );
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Cleanup function for the observer
  const cleanupObserver = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
  }, []);

  // Debounced version of setSelectedLocation
  const debouncedSetSelectedLocation = useCallback(
    debounce((location: Location) => {
      setSelectedLocation(location);
    }, 100),
    [setSelectedLocation]
  );

  // Throttled scroll handler
  const handleScroll = useCallback(
    throttle((e: React.UIEvent<HTMLDivElement>) => {
      // Manual check for visible locations on scroll
      if (isNavigating) return;

      const container = e.currentTarget;
      const containerRect = container.getBoundingClientRect();

      // Find all location elements that are visible
      locationRefs.current.forEach((ref, locationId) => {
        if (ref.current) {
          const elementRect = ref.current.getBoundingClientRect();
          const isVisible =
            elementRect.top < containerRect.bottom &&
            elementRect.bottom > containerRect.top &&
            elementRect.height > 0;

          if (isVisible) {
            logger.log(`Location ${locationId} is visible in scroll container`);

            // Find the location
            const location = selectedTrip?.locations.find(
              (loc) => loc.id === locationId
            );

            // Update selected location if it's not already selected and not an origin
            if (
              location &&
              !location.origin &&
              (!selectedLocation || selectedLocation.id !== locationId)
            ) {
              logger.log(
                `Manually setting selected location to ${location.name} from scroll event`
              );
              debouncedSetSelectedLocation(location);
            }
          }
        }
      });
    }, 150), // Reduced from 500ms to 150ms for faster response
    [
      selectedTrip?.locations,
      selectedLocation,
      debouncedSetSelectedLocation,
      isNavigating,
    ]
  );

  // Create refs for each location
  useEffect(() => {
    if (selectedTrip?.locations) {
      logger.log("Creating refs for locations:", selectedTrip.locations.length);

      // Clear previous refs
      locationRefs.current = new Map();

      // Create new refs for each location
      selectedTrip.locations.forEach((location) => {
        const newRef = React.createRef<HTMLDivElement>();
        locationRefs.current.set(location.id, newRef);
        logger.log("Created ref for location:", location.id);
      });

      // Force a re-initialization of the intersection observer
      if (observerRef.current) {
        cleanupObserver();
      }
    }
  }, [selectedTrip?.locations, cleanupObserver]);

  // Set default selected location to origin if none is selected
  useEffect(() => {
    if (selectedTrip?.locations?.length && !selectedLocation) {
      // Find the origin location (usually the first one)
      const originLocation = selectedTrip.locations[0];
      if (originLocation) {
        setSelectedLocation(originLocation);
      }
    }
  }, [selectedTrip?.locations, selectedLocation, setSelectedLocation]);

  // Set up intersection observer
  useEffect(() => {
    if (!selectedTrip?.locations) return;

    // Clean up any existing observer
    cleanupObserver();

    const observerOptions = {
      root: null,
      rootMargin: "20px", // Reduced from 50px to 20px for more precise detection
      threshold: 0.2, // Increased from 0.1 to 0.2 for better detection
    };

    const observerCallback: IntersectionObserverCallback = (entries) => {
      // Skip if we're currently navigating with buttons
      if (isNavigating) return;

      logger.log("Observer callback triggered with entries:", entries.length);

      entries.forEach((entry) => {
        logger.log(
          "Intersection entry:",
          entry.target.getAttribute("data-location-id"),
          entry.isIntersecting,
          "ratio:",
          entry.intersectionRatio
        );

        if (entry.isIntersecting) {
          // Find the location ID from the data attribute
          const locationId = entry.target.getAttribute("data-location-id");
          if (locationId) {
            const location = selectedTrip.locations.find(
              (loc) => loc.id === locationId
            );
            // Skip if this is the origin location
            if (
              location &&
              !location.origin &&
              (!selectedLocation || selectedLocation.id !== locationId)
            ) {
              logger.log(
                "Setting selected location from observer:",
                location.name
              );
              debouncedSetSelectedLocation(location);
            }
          }
        }
      });
    };

    // Create and store the observer
    observerRef.current = new IntersectionObserver(
      observerCallback,
      observerOptions
    );

    return cleanupObserver;
  }, [
    selectedTrip?.locations,
    selectedLocation,
    debouncedSetSelectedLocation,
    isNavigating,
    cleanupObserver,
  ]);

  // Reset observer when container changes
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Create a ResizeObserver to detect container size changes
    const resizeObserver = new ResizeObserver(() => {
      // Recreate the intersection observer when container size changes
      if (selectedTrip?.locations) {
        cleanupObserver();

        // We'll let the main effect recreate the observer on next render
        // This forces a re-evaluation of the intersection observer with the new container size
        if (selectedLocation) {
          setSelectedLocation(selectedLocation);
        }
      }
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [
    selectedTrip?.locations,
    cleanupObserver,
    selectedLocation,
    setSelectedLocation,
  ]);

  const { locationGroups } = useMemo(() => {
    if (!selectedTrip?.locations?.length) return { locationGroups: [] };

    return {
      locationGroups: selectedTrip.locations.map((location, index) => {
        let effectiveArrivalDate = null;
        if (location.id === selectedLocation?.id) {
          effectiveArrivalDate = getEffectiveArrivalDate(
            selectedTrip.transport,
            location.id
          );
        }

        if (!effectiveArrivalDate) {
          return {
            location,
            effectiveArrivalDate: DateTime.fromISO(
              location?.date.start_date || ""
            )
              .setZone(location?.date.time_zone || "")
              .set({ hour: 0, minute: 0 })
              .toISO(),
            previousLocation:
              index > 0 ? selectedTrip.locations[index - 1] : null,
            nextLocation:
              index < selectedTrip.locations.length - 1
                ? selectedTrip.locations[index + 1]
                : null,
            days: location.itinerary_items.map((item) => ({
              date: item.date,
            })),
          };
        }

        return {
          location,
          effectiveArrivalDate: effectiveArrivalDate || "",
          previousLocation:
            index > 0 ? selectedTrip.locations[index - 1] : null,
          nextLocation:
            index < selectedTrip.locations.length - 1
              ? selectedTrip.locations[index + 1]
              : null,
          days: location.itinerary_items.map((item) => ({
            date: item.date,
          })),
        };
      }),
    };
  }, [
    selectedTrip,
    getEffectiveArrivalDate,
    selectedLocation?.id,
    selectedLocation?.date.start_date,
    selectedLocation?.date.time_zone,
  ]);

  // Find transport between locations
  const getTransportBetweenLocations = (
    fromLocationId: string,
    toLocationId: string
  ) => {
    if (!selectedTrip?.transport) return null;

    return (
      selectedTrip.transport.find(
        (t) =>
          t.fromLocationId === fromLocationId && t.toLocationId === toLocationId
      ) || null
    );
  };

  if (!selectedTrip || !locationGroups.length) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-neutral-500">Select a trip to view the itinerary</p>
      </div>
    );
  }

  return (
    <div
      ref={scrollContainerRef}
      className="w-full h-full overflow-y-scroll no-scrollbar py-4 rounded-lg"
      style={{ maxHeight: "calc(100vh - 30px)" }}
      onScroll={handleScroll}
    >
      {locationGroups.map((group, locationIndex) => {
        const showTransportCard =
          locationIndex > 0 && !!locationGroups[locationIndex - 1];
        const previousLocationGroup = showTransportCard
          ? locationGroups[locationIndex - 1]
          : null;

        // Make sure we have a ref for this location
        if (!locationRefs.current.has(group.location.id)) {
          const newRef = React.createRef<HTMLDivElement>();
          locationRefs.current.set(group.location.id, newRef);
          logger.log(
            `Created new ref for location ${group.location.id} during render`
          );
        }

        // Get the ref for this location
        const locationRef = locationRefs.current.get(group.location.id)!;

        return (
          <React.Fragment key={group.location.id}>
            {showTransportCard && previousLocationGroup && (
              <TransportCard
                transport={getTransportBetweenLocations(
                  previousLocationGroup.location.id,
                  group.location.id
                )}
                fromLocation={previousLocationGroup.location}
                toLocation={group.location}
                handleTransportButtonClick={handleTransportButtonClick}
              />
            )}
            <LocationHeader
              location={group.location}
              dates={{
                start: group.location.date.start_date,
                end: group.location.date.end_date,
              }}
              isFirstLocation={locationIndex === 0}
              locationRef={locationRef}
              tripUsers={selectedTrip.users}
            />

            {!group.location.origin &&
              group.days.map((day, dayIndex) => (
                <DayView
                  key={day.date}
                  date={day.date}
                  locationTimeZone={group.location.date.time_zone}
                  isLastDay={dayIndex === group.days.length - 1}
                  selectedLocation={group.location}
                  nextLocation={group.nextLocation || null}
                  previousLocation={group.previousLocation || null}
                  handleTransportButtonClick={() => {}}
                  effectiveArrivalDate={group.effectiveArrivalDate || ""}
                />
              ))}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default TripItinerary;
